/**
 * Route line and card swatch colours. Each has at least 3:1 contrast (WCAG 1.4.11, graphical
 * objects) against white (light sheet, line casing, basemap) and #1c1c1e (dark sheet).
 */
export const ROUTE_COLORS = ["#0b66d6", "#a23fd6", "#c93400"];

/** Unselected routes are drawn at this opacity; it must still keep 3:1 (see the test). */
export const ROUTE_UNSELECTED_OPACITY = 0.85;

/**
 * Running-trail layer (ADR 0006), by trail kind. Green and teal stay clear of the route colours
 * (blue, purple, orange), the popularity heat (amber to magenta) and the basemap's own park
 * green. Each keeps 3:1 against white and the basemap land (see the test). Strength shows in the
 * width and dash, so kinds are not told apart by colour alone.
 */
export interface TrailStyle {
  color: string;
  width: number;
  /** MapLibre `line-dasharray` (in line widths); omitted for a solid line. */
  dash?: number[];
}

/** Kinds 1-3: elljusspar, motionsspar, running routes. */
export const TRAIL_STRONG: TrailStyle = { color: "#0a7a3b", width: 3.2 };
/** Kind 4: tracks. */
export const TRAIL_TRACK: TrailStyle = { color: "#4d7c0f", width: 2 };
/** Kinds 5 and 6: lit park paths and hiking routes. */
export const TRAIL_PATH: TrailStyle = { color: "#0f766e", width: 1.8, dash: [2, 1.5] };
/** Kind 7: long routes. */
export const TRAIL_LONG: TrailStyle = { color: "#52707a", width: 1.2, dash: [4, 2] };

export function trailStyle(kind: number): TrailStyle {
  if (kind >= 1 && kind <= 3) return TRAIL_STRONG;
  if (kind === 4) return TRAIL_TRACK;
  if (kind === 5 || kind === 6) return TRAIL_PATH;
  return TRAIL_LONG;
}

function luminance(hex: string): number {
  const lin = (i: number) => {
    const c = parseInt(hex.slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * lin(1) + 0.7152 * lin(3) + 0.0722 * lin(5);
}

/** WCAG contrast ratio between two #rrggbb colours. */
export function contrast(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (hi + 0.05) / (lo + 0.05);
}
